import { useMemo, useState } from 'react'
import { SizeGuideTabButtonText } from '@components/SizeGuideTabButtonText/SizeGuideTabButtonText'
import { SansText } from '@velasca/ui/sans-text'
import { Row, Size, Tab, TabContent, Table, Tabs } from './ShoeSizeTable.styles'
import type { SizeTableProps } from './ShoeSizeTable.types'

export const ShoeSizeTable = ({
  table = { imperial: [], metric: [], sizes: [] },
  convertible,
  firstButtonText,
  secondButtonText,
  localizedSizesLabel,
  localizedMeasure01,
  localizedMeasure02,
  defaultMeasurementSystem,
  firstCellFixed,
}: SizeTableProps) => {
  const [measurementSystem, setMeasurementSystem] = useState(
    defaultMeasurementSystem,
  )
  const { [defaultMeasurementSystem]: headers, sizes: metricSizes } = table

  const imperialSizes = useMemo(() => {
    return metricSizes?.map((row) =>
      row.map((size) => {
        if (typeof size === 'string') {
          return size
        }

        return Math.round((size / 2.54) * 10) / 10
      }),
    )
  }, [metricSizes])

  const sizes = measurementSystem === 'imperial' ? imperialSizes : metricSizes

  const localizeCell = (cell: string | number) => {
    switch (cell) {
      case 'localizedMeasure01': {
        return localizedMeasure01?.[measurementSystem] || ''
      }
      case 'localizedMeasure02': {
        return localizedMeasure02?.[measurementSystem] || ''
      }
      default: {
        return String(cell)
      }
    }
  }

  return (
    <>
      {convertible && (
        <Tabs>
          <Tab
            isSelected={measurementSystem === 'imperial'}
            order={defaultMeasurementSystem === 'metric' ? 0 : 1}
            onClick={() => {
              setMeasurementSystem('metric')
            }}
          >
            <SizeGuideTabButtonText>{firstButtonText}</SizeGuideTabButtonText>
          </Tab>
          <Tab
            isSelected={measurementSystem === 'metric'}
            order={defaultMeasurementSystem === 'imperial' ? 0 : 1}
            onClick={() => {
              setMeasurementSystem('imperial')
            }}
          >
            <SizeGuideTabButtonText>{secondButtonText}</SizeGuideTabButtonText>
          </Tab>
        </Tabs>
      )}
      <TabContent convertible={convertible}>
        <Table>
          {/* Prints header rows */}
          {headers?.map((header, i) => (
            <Row
              large
              showSeparator
              key={`header-${i}`}
              nColumn={header.length}
              convertible={convertible}
              firstCellFixed={firstCellFixed}
            >
              {header.map((unitMeasureCell, j) => (
                <Size key={`${unitMeasureCell}-${i}-${j}`}>
                  {i === 0 ? (
                    <SansText center color="primary" fontWeight="500">
                      {unitMeasureCell === 'localizedSizesLabel'
                        ? localizedSizesLabel
                        : String(unitMeasureCell)}
                    </SansText>
                  ) : (
                    <SansText center>
                      {unitMeasureCell === 'localizedSizesLabel'
                        ? localizedSizesLabel
                        : String(unitMeasureCell)}
                    </SansText>
                  )}
                </Size>
              ))}
            </Row>
          ))}

          {/* Prints body rows */}
          {sizes.map((row, i) => (
            <Row
              key={`size-${i}`}
              nColumn={row.length}
              convertible={convertible}
              firstCellFixed={firstCellFixed}
              large={i === 1}
            >
              {row.map((cell, j) => (
                <Size
                  key={`${cell}-${i}-${j}`}
                  highlight={j !== 0 && i !== 1}
                  secondary={j === 0 || i === 1}
                >
                  {j !== 0 && i !== 1 ? (
                    <SansText center color="primary">
                      {localizeCell(cell)}
                    </SansText>
                  ) : (
                    <SansText center fontWeight="500" color="gray">
                      {localizeCell(cell)}
                    </SansText>
                  )}
                </Size>
              ))}
            </Row>
          ))}
        </Table>
      </TabContent>
    </>
  )
}
